import { api } from '../api';
import { initAdminShell, escapeHtml, showError, confirmModal, setButtonBusy } from './shell';
import { attachSearch } from './search';
import { t } from '../i18n/i18n';

const ICON_EDIT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>';
const ICON_TRASH = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>';

interface UserRow {
  id: string;
  name: string;
  email: string;
  role: string;
  createdAt: string;
  isBlocked: boolean;
}

(async () => {
  try {
    await initAdminShell('users');
  } catch {
    return;
  }

  const tbody = document.getElementById('users-tbody');
  if (!tbody) return;

  let query = '';

  async function load(): Promise<void> {
    try {
      const client = await api();
      const { data } = await client.get<UserRow[]>('/api/users', {
        params: query ? { search: query } : {},
      });
      render(data);
    } catch (err) {
      showError('users-error', err instanceof Error ? err.message : 'Failed to load users.');
    }
  }

  function render(users: UserRow[]): void {
    if (!tbody) return;
    if (users.length === 0) {
      tbody.innerHTML = `<tr><td colspan="5" class="req-empty">${escapeHtml(t('admin.users.empty'))}</td></tr>`;
      return;
    }
    const yes = t('admin.users.yes');
    const no = t('admin.users.no');
    const editLabel = t('admin.actions.edit');
    const deleteLabel = t('admin.actions.delete');
    tbody.innerHTML = users.map(u => `
      <tr>
        <td>${escapeHtml(u.email)}</td>
        <td>${escapeHtml(u.name)}</td>
        <td>${escapeHtml(u.role)}</td>
        <td>${u.isBlocked ? escapeHtml(yes) : escapeHtml(no)}</td>
        <td class="row-actions">
          <a class="row-action" href="/admin-user-edit.html?id=${encodeURIComponent(u.id)}">${ICON_EDIT}<span>${escapeHtml(editLabel)}</span></a>
          <button type="button" class="row-action row-action--danger" data-id="${escapeHtml(u.id)}" data-email="${escapeHtml(u.email)}">${ICON_TRASH}<span>${escapeHtml(deleteLabel)}</span></button>
        </td>
      </tr>
    `).join('');

    tbody.querySelectorAll<HTMLButtonElement>('button.row-action--danger').forEach(btn => {
      btn.addEventListener('click', async () => {
        const id = btn.dataset.id!;
        const email = btn.dataset.email!;
        if (!(await confirmModal({
          title: t('admin.confirm.modal_title_delete'),
          message: t('admin.confirm.delete_user').replace('{email}', email),
          confirmLabel: t('admin.confirm.modal_confirm_delete'),
        }))) return;
        setButtonBusy(btn, true);
        try {
          const client = await api();
          await client.delete(`/api/users/${encodeURIComponent(id)}`);
          await load();
        } catch (err) {
          showError('users-error', err instanceof Error ? err.message : 'Delete failed.');
          setButtonBusy(btn, false);
        }
      });
    });
  }

  const searchInput = document.getElementById('users-search') as HTMLInputElement | null;
  if (searchInput) {
    attachSearch(searchInput, (q) => {
      query = q;
      void load();
    });
  }

  await load();
})();
